import { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi, blocksApi } from '../lib/api';
import { EMPTY_DOC, serializeNoteContent, deserializeNoteContent } from './blocks';

const DEFAULT_TITLE = 'Untitled note';
const SAVE_DEBOUNCE = 400;

const byOrder = (a, b) =>
  a.orderIndex - b.orderIndex || new Date(a.createdAt) - new Date(b.createdAt);

// The note's content is a single Tiptap document stored as one block.
function noteDocument(note) {
  const sortedBlocks = [...(note.blocks || [])].sort(byOrder);
  const first = sortedBlocks[0];
  return first ? deserializeNoteContent(first.content) : EMPTY_DOC;
}

function mapNote(note) {
  return {
    id: note.id,
    title: note.title,
    updatedAt: note.updatedAt,
    document: noteDocument(note),
  };
}

export function useNotes(token) {
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeNote, setActiveNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const titleTimer = useRef(null);
  const saveTimer = useRef(null);

  const loadNotes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await notesApi.list(token);
      setNotes(data.notes || []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Fetches the selected note (with its document) whenever activeId changes.
  useEffect(() => {
    if (!token || !activeId) {
      setActiveNote(null);
      return;
    }
    let cancelled = false;
    notesApi
      .get(activeId, token)
      .then((data) => {
        if (!cancelled) setActiveNote(mapNote(data.note));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load note');
      });
    return () => {
      cancelled = true;
    };
  }, [token, activeId]);

  const selectNote = useCallback((id) => setActiveId(id), []);

  const createNote = useCallback(async () => {
    if (!token) return;
    try {
      const { note } = await notesApi.create(DEFAULT_TITLE, token);
      // Give the new note an empty content block to hold its document.
      await blocksApi.create(
        { noteId: note.id, content: serializeNoteContent(EMPTY_DOC) },
        token
      );
      setNotes((prev) => [note, ...prev]);
      setActiveId(note.id);
      setActiveNote(mapNote({ ...note, blocks: [] }));
    } catch (e) {
      setError(e.message || 'Failed to create note');
    }
  }, [token]);

  const deleteNote = useCallback(
    async (id) => {
      if (!token) return;
      try {
        await notesApi.remove(id, token);
        setNotes((prev) => prev.filter((n) => n.id !== id));
        if (activeId === id) {
          setActiveId(null);
          setActiveNote(null);
        }
      } catch (e) {
        setError(e.message || 'Failed to delete note');
      }
    },
    [token, activeId]
  );

  const setTitle = useCallback(
    (noteId, title) => {
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, title } : n)));
      setActiveNote((prev) => (prev && prev.id === noteId ? { ...prev, title } : prev));
      if (!token) return;

      clearTimeout(titleTimer.current);
      titleTimer.current = setTimeout(() => {
        notesApi
          .update(noteId, title, token)
          .then((data) => {
            setNotes((prev) => prev.map((n) => (n.id === noteId ? data.note : n)));
          })
          .catch((e) => setError(e.message || 'Failed to update title'));
      }, SAVE_DEBOUNCE);
    },
    [token]
  );

  // Saves the note's document to its content block, debounced while typing.
  const updateDocument = useCallback(
    (noteId, document) => {
      setActiveNote((prev) => (prev && prev.id === noteId ? { ...prev, document } : prev));
      if (!token || activeId !== noteId) return;

      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          const { note } = await notesApi.get(noteId, token);
          const firstBlock = [...(note.blocks || [])].sort(byOrder)[0];
          const content = serializeNoteContent(document);
          if (firstBlock) {
            await blocksApi.update(firstBlock.id, { content }, token);
          } else {
            await blocksApi.create({ noteId, content }, token);
          }
        } catch (e) {
          setError(e.message || 'Failed to save note');
        }
      }, SAVE_DEBOUNCE);
    },
    [token, activeId]
  );

  return {
    notes,
    activeId,
    activeNote,
    loading,
    error,
    setActiveId: selectNote,
    createNote,
    deleteNote,
    setTitle,
    updateDocument,
  };
}
